import React from 'react';
import {
  Box, Container, Grid, Typography, Link, IconButton, Divider, alpha, useTheme, Chip, Stack,
} from '@mui/material';
import {Facebook as FacebookIcon, Twitter as TwitterIcon, LinkedIn as LinkedInIcon,
  Email as EmailIcon, Phone as PhoneIcon, LocationOn as LocationIcon, Security as SecurityIcon,
  Instagram as InstagramIcon, YouTube as YouTubeIcon, ArrowUpward as ArrowUpwardIcon,
  CarRental as CarRentalIcon, Inventory as InventoryIcon, TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const Footer: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const footerLinks = {
    Solutions: [
      { label: 'Inventory Tracking', href: '#features' },
      { label: 'Sales Management', href: '#features' },
      { label: 'Profit Analytics', href: '#process' },
      { label: 'Stock Optimization', href: '#features' },
    ],
    Company: [
      { label: 'About Sadaka Enterprise 1', href: '/about' },
      { label: 'Contact', href: '/contact' },
      { label: 'Careers', href: '#' },
      { label: 'Partners', href: '#' },
    ],
    Legal: [
      { label: 'Privacy Policy', href: '/privacy' },
      { label: 'Terms of Service', href: '/terms' },
      { label: 'Cookie Policy', href: '#' },
      { label: 'Data Security', href: '#' },
    ],
    Support: [
      { label: 'Help Center', href: '/support' },
      { label: 'Documentation', href: '#' },
      { label: 'API Status', href: '#' },
      { label: '24/7 Support', href: '/contact' },
    ],
  };

  const socialLinks = [
    { icon: <FacebookIcon />, href: '#', label: 'Facebook', color: '#1877F2' },
    { icon: <TwitterIcon />, href: '#', label: 'Twitter', color: '#1DA1F2' },
    { icon: <LinkedInIcon />, href: '#', label: 'LinkedIn', color: '#0A66C2' },
    { icon: <InstagramIcon />, href: '#', label: 'Instagram', color: '#E4405F' },
    { icon: <YouTubeIcon />, href: '#', label: 'YouTube', color: '#FF0000' },
  ];

  const contactInfo = [
    { icon: <PhoneIcon />, text: '+880 2 1234 5678', href: 'tel:+88021234567' },
    { icon: <EmailIcon />, text: 'support@sadaka.enterprise1.com', href: 'mailto:support@sadaka.enterprise1.com' },
    { icon: <LocationIcon />, text: 'Dhaka, Bangladesh', href: '#' },
  ];

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Box
      component="footer"
      sx={{
        background: 'linear-gradient(135deg, #0a0f1c 0%, #0f1424 50%, #1a1f35 100%)',
        color: 'white',
        pt: { xs: 8, md: 10 },
        pb: 3,
        mt: 'auto',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: 'linear-gradient(90deg, transparent 0%, rgba(102, 126, 234, 0.5) 50%, transparent 100%)',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: '-100px',
          right: '-100px',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(102, 126, 234, 0.1) 0%, transparent 70%)',
          pointerEvents: 'none',
        }
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={{ xs: 4, md: 6 }}>
          {/* Brand Section */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 3,
                cursor: 'pointer',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'translateX(5px)',
                }
              }}
              onClick={scrollToTop}
            >
              <Box
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2,
                  boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)',
                }}
              >
                <CarRentalIcon sx={{ fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                  Sadaka
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8, fontWeight: 600 }}>
                  Enterprise 1
                </Typography>
              </Box>
            </Box>
            
            <Typography variant="body2" sx={{ mb: 4, opacity: 0.85, lineHeight: 1.7, pr: { md: 2 } }}>
              Complete vehicle inventory management solution for automotive businesses. 
              Track purchases, sales, stock levels, and profit/loss analytics in real-time.
            </Typography>
            
            {/* Contact Info */}
            <Stack spacing={2} sx={{ mb: 4 }}>
              {contactInfo.map((info, index) => (
                <Link
                  key={index}
                  href={info.href}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    color: 'white',
                    textDecoration: 'none',
                    opacity: 0.85,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      opacity: 1,
                      transform: 'translateX(5px)',
                      color: '#667eea',
                    }
                  }}
                >
                  <Box 
                    sx={{ 
                      mr: 2, 
                      width: 36,
                      height: 36,
                      borderRadius: '8px',
                      background: alpha('#fff', 0.08),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '& svg': { fontSize: 18 }
                    }}
                  >
                    {info.icon}
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {info.text}
                  </Typography>
                </Link>
              ))}
            </Stack>

            {/* Social Links */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, opacity: 0.9 }}>
                Follow Us
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                {socialLinks.map((social, index) => (
                  <IconButton
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      width: 44,
                      height: 44,
                      color: 'white',
                      background: alpha('#fff', 0.08),
                      border: `1px solid ${alpha('#fff', 0.1)}`,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        background: social.color,
                        borderColor: social.color,
                        transform: 'translateY(-4px)',
                        boxShadow: `0 8px 20px ${alpha(social.color, 0.4)}`,
                      },
                    }}
                    aria-label={social.label}
                  >
                    {social.icon}
                  </IconButton>
                ))}
              </Box>
            </Box>
          </Grid>

          {/* Links Sections */}
          {Object.entries(footerLinks).map(([category, links]) => (
            <Grid size={{ xs: 6, sm: 6, md: 2 }} key={category}>
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 3, 
                  fontWeight: 700,
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  position: 'relative',
                  display: 'inline-block',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -8,
                    left: 0,
                    width: '30px',
                    height: '3px',
                    background: 'linear-gradient(90deg, #667eea, transparent)',
                    borderRadius: '2px',
                  }
                }}
              >
                {category}
              </Typography>
              <Stack spacing={1.5} component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                {links.map((link, index) => (
                  <Box component="li" key={index}>
                    <Link
                      href={link.href}
                      onClick={(e) => {
                        if (link.href.startsWith('#')) {
                          e.preventDefault();
                          const element = document.getElementById(link.href.substring(1));
                          if (element) {
                            element.scrollIntoView({ behavior: 'smooth' });
                          }
                        } else if (link.href.startsWith('/')) {
                          e.preventDefault();
                          navigate(link.href);
                        }
                      }}
                      sx={{
                        color: alpha('#fff', 0.75),
                        textDecoration: 'none',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        display: 'inline-block',
                        position: 'relative',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          color: 'white',
                          transform: 'translateX(5px)',
                          '&::before': {
                            width: '100%',
                          }
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          bottom: -2,
                          left: 0,
                          width: '0%',
                          height: '2px',
                          background: '#667eea',
                          transition: 'width 0.3s ease',
                        }
                      }}
                    >
                      {link.label}
                    </Link>
                  </Box>
                ))}
              </Stack>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: { xs: 6, md: 8 }, borderColor: alpha('#fff', 0.1) }} />

        {/* Bottom Bar */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'center', md: 'center' },
            gap: { xs: 3, md: 2 },
            textAlign: { xs: 'center', md: 'left' },
          }}
        >
          <Typography variant="body2" sx={{ opacity: 0.7, fontSize: '0.875rem' }}>
            © {new Date().getFullYear()} Sadaka Enterprise 1. All rights reserved.
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, md: 3 }, justifyContent: 'center' }}>
            <Typography variant="body2" sx={{ opacity: 0.7, fontSize: '0.875rem' }}>
              Designed & Developed by{' '}
              <Link
                href="https://www.aimarttech.com/"
                target="_blank"
                sx={{ color: 'inherit', textDecoration: 'none', fontWeight: 600 }}
              >
                AImart Tech
              </Link>
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;